import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PeopleListContainer from "./PeopleListContainer";
import { ContactDialog } from "./ContactDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { usePersonInvitations } from "@/shared/hooks/useEstateInvitations";
import { useAdminSimulation } from "@/shared/hooks/useAdminSimulation";
import { trackEvent, EventType } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { enqueueSnackbar } from "notistack";

import type { PersonContact } from "@/shared/types/estateContact";
import { formatContactName, getVisibleContacts } from "@/shared/utils/contactUtils";

const formatName = formatContactName;

interface PeopleListProps {
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
}

export default function PeopleList({ deceasedUserData }: PeopleListProps) {
  const queryClient = useQueryClient();
  const { authUser, person } = useAppContext();
  const { contacts, isLoading, accountOwner, updateContactAsync } = useUserContacts();
  const { isSimulating } = useAdminSimulation();
  const {
    invitations,
    acceptedInvitations,
    sendInvitation,
    resendInvitation,
    cancelInvitation,
    resendingInvitationEmail,
    isSendingInvitation,
  } = usePersonInvitations(person?.estate_id);

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = React.useState("");
  const [isAddContactOpen, setIsAddContactOpen] = React.useState(false);
  const [addedContactId, setAddedContactId] = React.useState<number | null>(null);
  const addedContact = addedContactId
    ? (contacts.find((c) => c.id === addedContactId) ?? null)
    : null;
  const [isAttorneyContactOpen, setIsAttorneyContactOpen] = React.useState(false);

  useEffect(() => {
    if (searchParams.get("add") === "attorney") {
      setIsAttorneyContactOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const allContacts = React.useMemo(
    () =>
      getVisibleContacts(contacts || [], {
        currentUserEmail: authUser?.email,
        accountOwnerEmail: accountOwner?.email,
        excludeEmail: isSimulating ? authUser?.email : undefined,
      }),
    [contacts, authUser?.email, accountOwner?.email, isSimulating],
  );

  // Search-filtered contacts
  const processedContacts = React.useMemo(() => {
    if (!searchQuery) return allContacts;

    return allContacts.filter((contact: PersonContact) => {
      const fullName = formatName(
        contact.first_name,
        contact.last_name,
        contact.middle_name,
      ).toLowerCase();
      const email = (contact.email || "").toLowerCase();
      const phone = (contact.phone || "").toLowerCase();
      const relationship = (contact.relationship || "").toLowerCase();
      const query = searchQuery.toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        relationship.includes(query)
      );
    });
  }, [allContacts, searchQuery]);

  const handleInviteContact = async (contact: PersonContact) => {
    if (!contact.email) {
      enqueueSnackbar("Add an email address to this contact before inviting them.", {
        variant: "info",
      });
      return;
    }

    if (!person?.estate_id) {
      enqueueSnackbar(
        "Finish setting up the estate before inviting family members. Contact support if this persists.",
        { variant: "info" },
      );
      return;
    }

    if (isSendingInvitation) return;

    try {
      const isAttorney = contact.relationship === "attorney";
      await sendInvitation.mutateAsync({
        estateId: person.estate_id,
        email: contact.email,
        role: isAttorney ? "admin" : "member",
        isAttorney,
      });
    } catch (error) {
      console.error("Failed to send invitation:", error);
      sentryUtils.captureError(error, "Failed to create invitation", {
        estateId: person?.estate_id,
      });
    }
  };

  const handleResendInvitation = async (contact: PersonContact) => {
    if (!contact.email) return;

    const pendingInvitation = invitations?.find((inv) => inv.email === contact.email);

    try {
      if (pendingInvitation) {
        // Resend existing invitation
        await resendInvitation.mutateAsync({
          invitationId: pendingInvitation.id,
          email: contact.email,
        });
      } else if (contact.invited && person?.estate_id) {
        // Contact was invited but email changed - send new invitation
        await sendInvitation.mutateAsync({
          estateId: person.estate_id,
          email: contact.email,
          role: "member",
        });
      }
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      sentryUtils.captureError(error, "Failed to resend invitation", {
        estateId: person?.estate_id,
      });
    }
  };

  const handleAddContact = () => {
    trackEvent(EventType.ADD_CONTACT_FORM_OPENED, {
      source: "people_list",
    });
    setIsAddContactOpen(true);
  };

  const handleAddAttorney = () => {
    if (isSimulating) return;
    trackEvent(EventType.ADD_CONTACT_FORM_OPENED, {
      source: "attorney_banner",
    });
    setIsAttorneyContactOpen(true);
  };

  const handleAttorneyContactAdded = () => {
    setIsAttorneyContactOpen(false);
  };

  return (
    <>
      <PeopleListContainer
        contacts={processedContacts}
        allContacts={allContacts}
        isLoading={isLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        deceasedUserData={deceasedUserData}
        onInviteContact={handleInviteContact}
        onResendInvitation={handleResendInvitation}
        onCancelInvitation={async (id) => cancelInvitation.mutateAsync(id)}
        onRemoveAccess={async (email: string) => {
          if (!person?.estate_id) return;
          const normalizedEmail = email.toLowerCase();

          const prevAccepted = queryClient.getQueryData<unknown[]>([
            "personInvitationsAccepted",
            person.estate_id,
          ]);
          const contactsKey = ["person-contacts", person.estate_id, authUser?.email];
          const prevContacts = queryClient.getQueryData<unknown[]>(contactsKey);

          queryClient.setQueryData<unknown[]>(
            ["personInvitationsAccepted", person.estate_id],
            (old = []) =>
              old.filter(
                (inv) => (inv as { email?: string }).email?.toLowerCase() !== normalizedEmail,
              ),
          );
          queryClient.setQueryData<unknown[]>(contactsKey, (old = []) =>
            old.map((c) =>
              (c as { email?: string }).email?.toLowerCase() === normalizedEmail
                ? { ...(c as object), invited: false }
                : c,
            ),
          );

          try {
            const { error } = await supabase.functions.invoke("account/remove-estate-access", {
              body: { estateId: person.estate_id, contactEmail: email },
            });
            if (error) throw error;
            enqueueSnackbar("Access removed", { variant: "success" });
          } catch {
            queryClient.setQueryData(["personInvitationsAccepted", person.estate_id], prevAccepted);
            queryClient.setQueryData(contactsKey, prevContacts);
            enqueueSnackbar("Couldn't remove access. Please try again.", { variant: "error" });
          } finally {
            queryClient.invalidateQueries({ queryKey: ["person-contacts", person.estate_id] });
            queryClient.invalidateQueries({
              queryKey: ["personInvitationsAccepted", person.estate_id],
            });
            queryClient.invalidateQueries({ queryKey: ["personInvitations", person.estate_id] });
          }
        }}
        pendingInvitations={invitations || []}
        acceptedInvitations={acceptedInvitations || []}
        currentUserEmail={authUser?.email}
        accountOwner={accountOwner}
        resendingInvitationEmail={resendingInvitationEmail}
        isSendingInvitation={isSendingInvitation}
        onAddContact={handleAddContact}
        onAddAttorney={handleAddAttorney}
        isSimulating={isSimulating}
      />

      <ContactDialog
        isOpen={isAddContactOpen}
        onOpenChange={(open) => {
          setIsAddContactOpen(open);
          if (!open) setAddedContactId(null);
        }}
        contact={addedContact}
        deceasedUserData={deceasedUserData}
        onContactAdded={async (c) => {
          await queryClient.refetchQueries({ queryKey: ["personInvitations", person?.estate_id] });
          setAddedContactId(c.id);
        }}
        onInvite={async (email, isAttorney) => {
          if (!person?.estate_id) return;
          if (addedContact && addedContact.email !== email) {
            await updateContactAsync({ id: addedContact.id, email });
          }
          await sendInvitation.mutateAsync({
            estateId: person.estate_id,
            email,
            role: isAttorney ? "admin" : "member",
            isAttorney,
          });
        }}
        pendingInvitation={
          addedContact?.email
            ? (() => {
                const inv = invitations?.find((i) => i.email === addedContact.email);
                return inv ? { id: inv.id, email: inv.email } : null;
              })()
            : null
        }
        onResendInvite={async (_id, email) => {
          const inv = invitations?.find((i) => i.email === email);
          if (inv) await resendInvitation.mutateAsync({ invitationId: inv.id, email });
        }}
        onCancelInvite={async (id) => cancelInvitation.mutateAsync(id)}
        isInviting={isSendingInvitation}
      />

      <ContactDialog
        isOpen={isAttorneyContactOpen}
        onOpenChange={setIsAttorneyContactOpen}
        deceasedUserData={deceasedUserData}
        onContactAdded={handleAttorneyContactAdded}
        initialRelationship="attorney"
        hideAddress={true}
        onInvite={async (email, isAttorney) => {
          if (!person?.estate_id) return;
          await sendInvitation.mutateAsync({
            estateId: person.estate_id,
            email,
            role: isAttorney ? "admin" : "member",
            isAttorney: isAttorney ?? true,
          });
        }}
      />
    </>
  );
}
